/**
 * GTM Analytics Utility for Valley Eats Courier Hub
 *
 * This utility manages all analytics tracking through Google Tag Manager's data layer.
 * It provides a type-safe interface for tracking events across the courier application.
 */

// Extend Window interface to include dataLayer
declare global {
  interface Window {
    dataLayer: any[];
  }
}

/**
 * Initialize GTM data layer
 */
export const initDataLayer = (): void => {
  window.dataLayer = window.dataLayer || [];
};

/**
 * Push event to GTM data layer
 */
export const pushToDataLayer = (data: Record<string, any>): void => {
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push(data);

    // Debug mode: log to console if enabled
    if (process.env.REACT_APP_ANALYTICS_DEBUG === 'true') {
      console.log('[Analytics]', data);
    }
  }
};

/**
 * Track generic event
 */
export const trackEvent = (eventName: string, eventData?: Record<string, any>): void => {
  pushToDataLayer({
    event: eventName,
    ...eventData,
    timestamp: new Date().toISOString(),
  });
};

/**
 * Track page view
 */
export const trackPageView = (pageName: string, additionalData?: Record<string, any>): void => {
  trackEvent('page_view', {
    page_name: pageName,
    page_path: window.location.pathname,
    page_url: window.location.href,
    referrer: document.referrer,
    ...additionalData,
  });
};

/**
 * Track application funnel events
 */
export const trackApplicationStarted = (data: {
  email: string;
  applicantId: string;
  entrySource?: string;
}): void => {
  trackEvent('application_started', {
    category: 'Conversion',
    email: data.email,
    applicant_id: data.applicantId,
    entry_source: data.entrySource || 'direct',
  });
};

export const trackWizardStepView = (data: {
  stepName: string;
  stepNumber: number;
  progressPercent: number;
}): void => {
  trackEvent('wizard_step_view', {
    category: 'Conversion',
    step_name: data.stepName,
    step_number: data.stepNumber,
    progress_percent: data.progressPercent,
  });
};

export const trackWizardStepComplete = (data: {
  stepName: string;
  timeOnStep: number;
  completionStatus: string;
}): void => {
  trackEvent('wizard_step_complete', {
    category: 'Conversion',
    step_name: data.stepName,
    time_on_step: data.timeOnStep,
    completion_status: data.completionStatus,
  });
};

export const trackFitAssessmentComplete = (data: {
  answers: {
    daysPerWeek: string;
    commitmentLevel: number;
    workStyle: string;
    motivation: string;
    hasReliableTransportation: boolean;
  };
}): void => {
  trackEvent('fit_assessment_complete', {
    category: 'Conversion',
    ...data.answers,
  });
};

export const trackAgreementAccepted = (data: {
  agreementViewed: boolean;
  timeToAccept: number;
}): void => {
  trackEvent('agreement_accepted', {
    category: 'Conversion',
    agreement_viewed: data.agreementViewed,
    time_to_accept: data.timeToAccept,
  });
};

export const trackQuizStart = (quizVersion?: string): void => {
  trackEvent('local_knowledge_quiz_start', {
    category: 'Conversion',
    quiz_version: quizVersion || 'v1',
  });
};

export const trackQuizComplete = (data: {
  score: number;
  correctAnswers: number;
  incorrectAnswers: number;
  timeTaken: number;
}): void => {
  trackEvent('local_knowledge_quiz_complete', {
    category: 'Conversion',
    score: data.score,
    correct_answers: data.correctAnswers,
    incorrect_answers: data.incorrectAnswers,
    time_taken: data.timeTaken,
  });
};

export const trackVideoIntroComplete = (data: {
  videoType: 'recorded' | 'uploaded';
  videoDuration?: number;
  attemptNumber: number;
}): void => {
  trackEvent('video_intro_complete', {
    category: 'Conversion',
    video_type: data.videoType,
    video_duration: data.videoDuration,
    attempt_number: data.attemptNumber,
  });
};

export const trackDocumentsUploaded = (data: {
  documentTypes: string[];
  uploadCount: number;
  timeTaken: number;
}): void => {
  trackEvent('documents_uploaded', {
    category: 'Conversion',
    document_types: data.documentTypes.join(','),
    upload_count: data.uploadCount,
    time_taken: data.timeTaken,
  });
};

export const trackInterviewScheduled = (data: {
  selectedDate: string;
  selectedTime: string;
  bookingSource?: string;
}): void => {
  trackEvent('interview_scheduled', {
    category: 'Conversion',
    selected_date: data.selectedDate,
    selected_time: data.selectedTime,
    booking_source: data.bookingSource || 'wizard',
  });
};

export const trackContractSigned = (data: {
  signatureType: string;
  contractVersion?: string;
}): void => {
  trackEvent('contract_signed', {
    category: 'Conversion',
    signature_type: data.signatureType,
    contract_version: data.contractVersion || 'v1',
  });
};

export const trackOnboardingComplete = (data: {
  totalTime: number;
  stepsCompleted: number;
  completionDate: string;
}): void => {
  trackEvent('onboarding_complete', {
    category: 'Conversion',
    total_time: data.totalTime,
    steps_completed: data.stepsCompleted,
    completion_date: data.completionDate,
  });
};

/**
 * Track form interactions
 */
export const trackFormStart = (formName: string, pageLocation: string): void => {
  trackEvent('form_start', {
    category: 'Form_Interaction',
    form_name: formName,
    page_location: pageLocation,
  });
};

export const trackFormFieldComplete = (formName: string, fieldName: string): void => {
  trackEvent('form_field_complete', {
    category: 'Form_Interaction',
    form_name: formName,
    field_name: fieldName,
  });
};

export const trackFormSubmit = (data: {
  formName: string;
  success: boolean;
  errorMessage?: string;
}): void => {
  trackEvent('form_submit', {
    category: 'Form_Interaction',
    form_name: data.formName,
    success: data.success,
    error_message: data.errorMessage,
  });
};

export const trackFormAbandon = (data: {
  formName: string;
  fieldsCompleted: number;
  fieldsRemaining: number;
}): void => {
  trackEvent('form_abandon', {
    category: 'Form_Interaction',
    form_name: data.formName,
    fields_completed: data.fieldsCompleted,
    fields_remaining: data.fieldsRemaining,
  });
};

/**
 * Track button clicks
 */
export const trackButtonClick = (data: {
  buttonName: string;
  buttonLocation: string;
  pageName: string;
  destinationUrl?: string;
}): void => {
  trackEvent('button_click', {
    category: 'User_Engagement',
    button_name: data.buttonName,
    button_location: data.buttonLocation,
    page_name: data.pageName,
    destination_url: data.destinationUrl,
  });
};

export const trackCTAClick = (ctaName: string, ctaLocation: string): void => {
  trackEvent('cta_click', {
    category: 'User_Engagement',
    cta_name: ctaName,
    cta_location: ctaLocation,
  });
};

/**
 * Track video interactions
 */
export const trackVideoRecordStart = (attemptNumber: number): void => {
  trackEvent('video_record_start', {
    category: 'Media_Engagement',
    attempt_number: attemptNumber,
  });
};

export const trackVideoRecordStop = (duration: number, attemptNumber: number): void => {
  trackEvent('video_record_stop', {
    category: 'Media_Engagement',
    duration,
    attempt_number: attemptNumber,
  });
};

export const trackVideoRecordRetake = (previousAttemptDuration: number): void => {
  trackEvent('video_record_retake', {
    category: 'Media_Engagement',
    previous_attempt_duration: previousAttemptDuration,
  });
};

export const trackVideoUploadStart = (fileSize: number, fileType: string): void => {
  trackEvent('video_upload_start', {
    category: 'Media_Engagement',
    file_size: fileSize,
    file_type: fileType,
  });
};

export const trackVideoUploadComplete = (fileSize: number, uploadDuration: number): void => {
  trackEvent('video_upload_complete', {
    category: 'Media_Engagement',
    file_size: fileSize,
    upload_duration: uploadDuration,
  });
};

export const trackVideoUploadError = (errorType: string, errorMessage: string): void => {
  trackEvent('video_upload_error', {
    category: 'Media_Engagement',
    error_type: errorType,
    error_message: errorMessage,
  });
};

/**
 * Track document uploads
 */
export const trackDocumentUploadStart = (documentType: string): void => {
  trackEvent('document_upload_start', {
    category: 'Document_Interaction',
    document_type: documentType,
  });
};

export const trackDocumentUploadComplete = (data: {
  documentType: string;
  fileSize: number;
  uploadDuration: number;
}): void => {
  trackEvent('document_upload_complete', {
    category: 'Document_Interaction',
    document_type: data.documentType,
    file_size: data.fileSize,
    upload_duration: data.uploadDuration,
  });
};

export const trackDocumentUploadError = (data: {
  documentType: string;
  errorType: string;
  errorMessage: string;
}): void => {
  trackEvent('document_upload_error', {
    category: 'Document_Interaction',
    document_type: data.documentType,
    error_type: data.errorType,
    error_message: data.errorMessage,
  });
};

export const trackDocumentValidationError = (documentType: string, validationError: string): void => {
  trackEvent('document_validation_error', {
    category: 'Document_Interaction',
    document_type: documentType,
    validation_error: validationError,
  });
};

/**
 * Track modal interactions
 */
export const trackModalOpen = (modalName: string, triggerSource: string): void => {
  trackEvent('modal_open', {
    category: 'Modal_Interaction',
    modal_name: modalName,
    trigger_source: triggerSource,
  });
};

export const trackModalClose = (data: {
  modalName: string;
  timeViewed: number;
  closeMethod: string;
}): void => {
  trackEvent('modal_close', {
    category: 'Modal_Interaction',
    modal_name: data.modalName,
    time_viewed: data.timeViewed,
    close_method: data.closeMethod,
  });
};

export const trackAgreementModalView = (viewSource: string): void => {
  trackEvent('agreement_modal_view', {
    category: 'Modal_Interaction',
    view_source: viewSource,
  });
};

export const trackAgreementScroll = (scrollDepth: number, sectionsViewed: number): void => {
  trackEvent('agreement_scroll', {
    category: 'Modal_Interaction',
    scroll_depth: scrollDepth,
    sections_viewed: sectionsViewed,
  });
};

/**
 * Track navigation
 */
export const trackExternalLinkClick = (linkUrl: string, linkText: string): void => {
  trackEvent('external_link_click', {
    category: 'Navigation',
    link_url: linkUrl,
    link_text: linkText,
    destination: linkUrl,
  });
};

export const trackEmailLinkClick = (emailAddress: string): void => {
  trackEvent('email_link_click', {
    category: 'Navigation',
    email_address: emailAddress,
  });
};

/**
 * Track errors
 */
export const trackAPIError = (data: {
  endpoint: string;
  errorCode: string | number;
  errorMessage: string;
}): void => {
  trackEvent('api_error', {
    category: 'Error',
    endpoint: data.endpoint,
    error_code: data.errorCode,
    error_message: data.errorMessage,
  });
};

export const trackValidationError = (fieldName: string, errorType: string): void => {
  trackEvent('validation_error', {
    category: 'Error',
    field_name: fieldName,
    error_type: errorType,
  });
};

export const trackNavigationBlock = (blockedReason: string, currentStep: string): void => {
  trackEvent('navigation_block', {
    category: 'Error',
    blocked_reason: blockedReason,
    current_step: currentStep,
  });
};

/**
 * Initialize GTM script
 */
export const initGTM = (gtmId: string): void => {
  if (typeof window === 'undefined' || !gtmId) {
    return;
  }

  // Initialize data layer
  initDataLayer();

  // Check if GTM is already loaded
  if (document.querySelector(`script[src*="${gtmId}"]`)) {
    return;
  }

  // Add GTM script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;
  document.head.appendChild(script);

  // Add noscript iframe
  const noscript = document.createElement('noscript');
  const iframe = document.createElement('iframe');
  iframe.src = `https://www.googletagmanager.com/ns.html?id=${gtmId}`;
  iframe.height = '0';
  iframe.width = '0';
  iframe.style.display = 'none';
  iframe.style.visibility = 'hidden';
  noscript.appendChild(iframe);
  document.body.insertBefore(noscript, document.body.firstChild);

  console.log('[Analytics] GTM initialized with ID:', gtmId);
};
